import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CustomErrorHandlerService } from './custom-error-handler.service';
import { AmplifyService } from './amplify.service';
import { environment } from 'src/environments/environment';
import { v4 as uuid } from 'uuid';

const { Buffer } = require('buffer');

/** Maps the stored weekDay values to EventBridge cron day-of-week tokens. */
const DAY_MAP: Record<string, string> = {
  sunday: 'SUN',
  monday: 'MON',
  tuesday: 'TUE',
  wednesday: 'WED',
  thursday: 'THU',
  friday: 'FRI',
  saturday: 'SAT'
};

/** Cron day used when a weekly cadence has no recognised weekDay. */
const DEFAULT_CRON_WEEK_DAY = 'MON';

/** Cron day-of-week tokens in week order, Sunday first, for shifting a day. */
const CRON_WEEK_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** Endpoint used for every partition-key read in this service. */
const DYNAMODB_INDEX_QUERY_PATH = '/dynamodb/get-documents-by-index';

// Defaults applied when a config reaches the API with a field left blank.
const DEFAULT_PRODUCT = 'QuickBridge';
const DEFAULT_REPORT_TYPE = 'PDF';
const DEFAULT_FILTER = 'Fail';
const DEFAULT_NOTIFICATION_TYPE = 'scheduled';
const DEFAULT_TIME_OF_DAY = '13:00';
const INITIAL_VERSION = '0';

/** An IANA time zone a notification schedule can be expressed in. */
export interface NotificationTimeZone {
  /** IANA identifier, stored on the config. */
  id: string;
  /** Standard-time abbreviation, for example 'EST'. */
  abbreviation: string;
  /**
   * Daylight-time abbreviation, for example 'EDT', for zones that observe
   * daylight saving. Such zones are offered as two fixed-offset options.
   */
  daylightAbbreviation?: string;
  /** Regions the zone covers, for example 'Eastern Time (US & Canada)'. */
  region: string;
  /**
   * Locale whose CLDR data knows this zone's short name, used only to look up a
   * date's DST-aware abbreviation. Omitted means en-US. See
   * NotificationService.getTimeZoneAbbreviation.
   */
  abbreviationLocale?: string;
  /** Label shown in the time zone dropdown, composed from the two fields above. */
  label: string;
}

/**
 * Time zones a notification schedule can be set to, ordered west to east.
 *
 * Deliberately a curated subset rather than the full IANA database, so the
 * dropdown stays usable. The `id` is what gets stored on the config, so a
 * selection persists as an IANA identifier such as 'Asia/Tokyo'.
 *
 * `abbreviation` is the zone's standard-time name and `daylightAbbreviation`
 * its daylight-time name. The dropdown comes from
 * NotificationService.getTimeZoneOptions, which offers a daylight-saving zone
 * as two fixed-offset options: "(AEST) Sydney" is UTC+10 all year and
 * "(AEDT) Sydney" is UTC+11 all year. A single option that followed DST let a
 * user enter a time against UTC+10 while Sydney was on UTC+11, an hour off.
 * Abbreviations are also not globally unique: CST covers both Central Time and
 * China Standard Time, so the region text beside it tells the two apart.
 *
 * The abbreviationLocale values match ABBREVIATION_LOCALE_BY_ZONE_PREFIX in the
 * email-service report formatter, so the dialog and the email agree. Update both
 * lists when adding a zone here.
 */
const TIME_ZONE_DEFINITIONS: readonly Omit<NotificationTimeZone, 'label'>[] = [
  { id: 'Pacific/Midway', abbreviation: 'SST', region: 'Midway Island' },
  { id: 'Pacific/Honolulu', abbreviation: 'HST', region: 'Hawaii' },
  { id: 'America/Anchorage', abbreviation: 'AKST', daylightAbbreviation: 'AKDT', region: 'Alaska' },
  { id: 'America/Los_Angeles', abbreviation: 'PST', daylightAbbreviation: 'PDT', region: 'Pacific Time (US & Canada)' },
  { id: 'America/Denver', abbreviation: 'MST', daylightAbbreviation: 'MDT', region: 'Mountain Time (US & Canada)' },
  { id: 'America/Chicago', abbreviation: 'CST', daylightAbbreviation: 'CDT', region: 'Central Time (US & Canada)' },
  { id: 'America/New_York', abbreviation: 'EST', daylightAbbreviation: 'EDT', region: 'Eastern Time (US & Canada)' },
  { id: 'America/Halifax', abbreviation: 'AST', daylightAbbreviation: 'ADT', region: 'Atlantic Time (Canada)' },
  { id: 'America/Argentina/Buenos_Aires', abbreviation: 'ART', region: 'Buenos Aires' },
  { id: 'Atlantic/Azores', abbreviation: 'AZOT', daylightAbbreviation: 'AZOST', region: 'Azores' },
  { id: 'UTC', abbreviation: 'UTC', region: 'Coordinated Universal Time' },
  { id: 'Europe/London', abbreviation: 'GMT', daylightAbbreviation: 'BST', region: 'London, Dublin, Lisbon', abbreviationLocale: 'en-GB' },
  { id: 'Europe/Paris', abbreviation: 'CET', daylightAbbreviation: 'CEST', region: 'Paris, Berlin, Madrid, Rome', abbreviationLocale: 'en-GB' },
  { id: 'Europe/Athens', abbreviation: 'EET', daylightAbbreviation: 'EEST', region: 'Athens, Cairo, Helsinki', abbreviationLocale: 'en-GB' },
  { id: 'Europe/Moscow', abbreviation: 'MSK', region: 'Moscow, Istanbul' },
  { id: 'Asia/Dubai', abbreviation: 'GST', region: 'Dubai, Abu Dhabi', abbreviationLocale: 'en-GB' },
  { id: 'Asia/Karachi', abbreviation: 'PKT', region: 'Karachi, Islamabad' },
  { id: 'Asia/Kolkata', abbreviation: 'IST', region: 'Mumbai, New Delhi', abbreviationLocale: 'en-IN' },
  { id: 'Asia/Dhaka', abbreviation: 'BST', region: 'Dhaka' },
  { id: 'Asia/Bangkok', abbreviation: 'ICT', region: 'Bangkok, Jakarta' },
  { id: 'Asia/Shanghai', abbreviation: 'CST', region: 'Beijing, Singapore, Hong Kong' },
  { id: 'Asia/Tokyo', abbreviation: 'JST', region: 'Tokyo, Seoul' },
  { id: 'Australia/Sydney', abbreviation: 'AEST', daylightAbbreviation: 'AEDT', region: 'Sydney, Melbourne', abbreviationLocale: 'en-AU' },
  { id: 'Pacific/Auckland', abbreviation: 'NZST', daylightAbbreviation: 'NZDT', region: 'Auckland, Fiji', abbreviationLocale: 'en-NZ' }
];

export const NOTIFICATION_TIME_ZONES: readonly NotificationTimeZone[] =
  TIME_ZONE_DEFINITIONS.map(definition => ({
    ...definition,
    label: `(${definition.abbreviation}) ${definition.region}`
  }));

/**
 * One selectable entry in the time zone dropdown.
 *
 * A daylight-saving zone yields two entries, one per abbreviation, each pinned
 * to a fixed offset. `value` tells them apart, since both share the zone `id`.
 */
export interface NotificationTimeZoneOption {
  /** Unique dropdown value, `${id}|${abbreviation}`. */
  value: string;
  /** IANA identifier, stored on the config as `timeZone`. */
  id: string;
  /** Abbreviation this option is pinned to, stored as `timeZoneAbbreviation`. */
  abbreviation: string;
  region: string;
  label: string;
  /**
   * Zone used to convert times for this option. A fixed-offset Etc zone for a
   * daylight-saving option (Etc/GMT-10 for AEST), otherwise the zone `id`.
   */
  conversionTimeZone: string;
}

/** Used when the machine's offset matches none of the supported options. */
export const FALLBACK_TIME_ZONE = 'America/New_York';

/**
 * The IANA Etc zone that is a fixed offset from UTC, for example Etc/GMT-10
 * for UTC+10. Etc signs are inverted by POSIX convention. Returns null for a
 * fractional-hour offset, which has no Etc zone (none of the daylight-saving
 * options here use one).
 */
function toFixedOffsetZone(offsetMinutes: number): string | null {
  const hours = offsetMinutes / 60;
  if (!Number.isInteger(hours)) {
    return null;
  }
  if (hours === 0) {
    return 'Etc/GMT';
  }
  return hours > 0 ? `Etc/GMT-${hours}` : `Etc/GMT+${-hours}`;
}

const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

const DEFAULT_ABBREVIATION_LOCALE = 'en-US';
const OFFSET_STYLE_ABBREVIATION = /^(?:GMT|UTC)[+-]/;
const ABBREVIATION_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

export interface NotificationConfig {
  assetID: string;
  notificationId: string;
  cognitoID: string;
  company: string;
  product: string;
  reportType: string;
  filter: string;
  notificationType: 'scheduled' | 'post-loan';
  emails: string[];
  isActive: boolean;
  schedule: string | null;
  reportWindow: string | null;
  lastSentAt: string | null;
  eventBridgeRuleArn: string | null;
  version: string;
  createdBy: string;
  createdAt: string;
  lastModified: string;
  time: string;
  /** IANA identifier the `time` above is expressed in. */
  timeZone: string;
  /**
   * Which abbreviation of `timeZone` the user picked, for example AEST or AEDT.
   * For a daylight-saving zone this pins `time` to that fixed offset.
   */
  timeZoneAbbreviation?: string;
  weekDay: string;
  timeInterval: string;
}

/** A single email delivery attempt recorded by the notifications backend. */
export interface DeliveryLog {
  notificationId: string;
  assetID: string;
  timestamp: string;
  status: string;
  recipientCount: number;
  reportType: string;
  correlationId: string;
  failureReason?: string;
}

/**
 * Fields common to both write routes.
 *
 * The update route identifies the record through the URL, so only the create
 * body carries assetID and notificationId. Keeping the rest in one place means
 * a new field cannot be added to one route and forgotten on the other.
 */
export interface NotificationRequestPayload {
  cognitoID: string;
  company: string;
  product: string;
  reportType: string;
  filter: string;
  notificationType: string;
  emails: string[];
  isActive: boolean;
  schedule: string | null;
  reportWindow: string | null;
  lastSentAt: string | null;
  eventBridgeRuleArn: string | null;
  version: string;
  createdBy: string;
  createdAt: string;
  lastModified: string;
  time: string;
  timeZone: string;
  timeZoneAbbreviation: string;
  weekDay: string;
  timeInterval: string;
}

/** Body of POST /notifications. */
export interface CreateNotificationRequest extends NotificationRequestPayload {
  assetID: string;
  notificationId: string;
}

/** Body of PUT /notifications/{assetID}/{notificationId}. */
export type UpdateNotificationRequest = NotificationRequestPayload;

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly currentEnvironment: 'prod' | 'test' | 'dev' = environment.production
    ? 'prod'
    : environment.test
    ? 'test'
    : 'dev';
  private readonly baseUrl: string;
  private readonly httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private http: HttpClient,
    private errorHandler: CustomErrorHandlerService,
    private amplifyService: AmplifyService
  ) {
    this.baseUrl = Buffer.from(environment.environmentURL, 'base64').toString();
  }

  /**
   * Builds HTTP options with a Bearer-prefixed Authorization header.
   * The notifications API requires the standard 'Bearer <token>' format,
   * unlike the legacy CCMC Authorizer which accepts a raw token.
   */
  private getAuthHeaders(): Observable<HttpHeaders> {
    return from(this.amplifyService.getAccessToken()).pipe(
      map((result: any) => {
        return new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${result.accessToken}`
        });
      })
    );
  }

  /** DynamoDB table holding notification configs for the current environment. */
  private get configTable(): string {
    return `${this.currentEnvironment}_notifications_config`;
  }

  /**
   * DynamoDB table holding notification delivery logs for the current environment.
   *
   * Logs are read through the DynamoDB index endpoint rather than
   * GET /notifications/{assetID}/logs because that GET route is not working
   * with the CCMC Authorizer.
   */
  private get deliveryLogTable(): string {
    return `${this.currentEnvironment}_notifications_delivery_log`;
  }

  /** Reads the item array out of the DynamoDB endpoint's response envelope. */
  private extractItems(response: any): any[] {
    return response?.content?.Items || response?.Items || (Array.isArray(response) ? response : []);
  }

  /**
   * Reads a DynamoDB table by its partition key through the shared
   * get-documents-by-index endpoint.
   *
   * Failures are left for the caller to handle, so each query can decide
   * whether an error should surface or degrade to an empty list.
   */
  private queryDocumentsByIndex(
    tableName: string,
    primaryKeyAttribute: string,
    primaryKey: string
  ): Observable<any[]> {
    return this.http
      .post(
        `${this.baseUrl}${DYNAMODB_INDEX_QUERY_PATH}`,
        { tableName, primaryKeyAttribute, primaryKey },
        this.httpOptions
      )
      .pipe(map(response => this.extractItems(response)));
  }

  /**
   * Lists notification configs for an asset, optionally narrowed to one user.
   *
   * NOTE: the client-side filter by user is a temporary workaround while the
   * GET endpoint has issues with the CCMC Authorizer. Ideally the backend would
   * scope this query by user (e.g. GSI on assetID+createdBy).
   */
  listConfigs(assetID: string, cognitoId?: string): Observable<any> {
    console.log(`[NotificationService] listConfigs for assetID=${assetID}, cognitoId=${cognitoId}`);
    return this.queryDocumentsByIndex(this.configTable, 'assetID', assetID).pipe(
      map(items =>
        cognitoId
          ? items.filter(
              (config: any) => config.cognitoID === cognitoId || config.cognitoId === cognitoId
            )
          : items
      ),
      catchError(this.errorHandler.handleError)
    );
  }

  /**
   * Lists delivery logs for every notification config owned by the given user.
   *
   * The delivery log table is partitioned by notificationId, so configs are
   * resolved first and then queried in parallel. Results are merged and sorted
   * newest first. A failed query for a single notificationId yields an empty
   * list rather than failing the whole request, so one bad config cannot hide
   * the remaining logs.
   */
  listLogs(assetID: string, cognitoId?: string): Observable<DeliveryLog[]> {
    console.log(`[NotificationService] listLogs for assetID=${assetID}, cognitoId=${cognitoId}`);
    return this.listConfigs(assetID, cognitoId).pipe(
      switchMap((configs: unknown) => {
        console.log('configs', configs);
        // listConfigs routes HTTP failures through the shared error handler,
        // which emits the HttpErrorResponse as a value instead of erroring.
        // A non-array payload therefore means the config lookup failed.
        if (!Array.isArray(configs)) {
          return throwError(() => new Error('Unable to load notification configs for delivery logs'));
        }

        const notificationIds = (configs as NotificationConfig[])
          .map(config => config.notificationId)
          .filter(notificationId => !!notificationId);

        if (notificationIds.length === 0) {
          return of([] as DeliveryLog[]);
        }

        return forkJoin(
          notificationIds.map(notificationId => this.getLogsByNotificationId(notificationId))
        ).pipe(map(logsPerConfig => this.mergeLogsNewestFirst(logsPerConfig)));
      })
    );
  }

  private getLogsByNotificationId(notificationId: string): Observable<DeliveryLog[]> {
    return this.queryDocumentsByIndex(this.deliveryLogTable, 'notificationId', notificationId).pipe(
      catchError(err => {
        console.error(`[NotificationService] Logs query failed for ${notificationId}`, err);
        return of([] as DeliveryLog[]);
      })
    );
  }

  private mergeLogsNewestFirst(logsPerConfig: DeliveryLog[][]): DeliveryLog[] {
    return logsPerConfig
      .flat()
      .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  }

  // ── Schedule & Time Utilities ──

  /**
   * Builds an AWS EventBridge cron expression and report window
   * from notification subscription form values.
   *
   * @param time      Local time string in "HH:mm" format
   * @param frequency 'instantly' | 'daily' | 'weekly' | 'bi-weekly'
   * @param weekDay   The selected weekday (e.g. 'monday'), required for 'weekly' and 'bi-weekly'
   * @returns         An object with `schedule` (cron expression) and `reportWindow`
   */
  buildSchedule(
    time: string,
    frequency: string,
    weekDay: string,
    timeZone?: string
  ): { schedule: string; reportWindow: string } {
    // Derived through the same converter that stores `time`, so the cron and the
    // persisted time can never disagree about which zone they came from.
    const { hours: utcHour, minutes: utcMinute } = this.splitHoursAndMinutes(
      this.convertLocalToUtcTime(time, timeZone)
    );

    switch (frequency) {
      case 'daily':
        return {
          schedule: `cron(${utcMinute} ${utcHour} * * ? *)`,
          reportWindow: '24h'
        };

      case 'weekly':
      case 'bi-weekly': {
        // EventBridge lacks native bi-weekly support, so both cadences fire on
        // the same weekly cron and only the report window tells them apart;
        // the alternation is tracked downstream.
        // EventBridge reads the weekday in UTC, so it moves with the time when
        // the UTC conversion crosses midnight: Tuesday 11:54 PM Eastern is
        // Wednesday 03:54 UTC.
        const cronWeekDay = this.shiftCronWeekDay(
          DAY_MAP[weekDay] || DEFAULT_CRON_WEEK_DAY,
          this.getUtcDayShift(time, timeZone)
        );
        return {
          schedule: `cron(${utcMinute} ${utcHour} ? * ${cronWeekDay} *)`,
          reportWindow: frequency === 'bi-weekly' ? '14d' : '7d'
        };
      }

      default:
        // 'instantly' or unknown — event-driven, no cron schedule needed
        return { schedule: '', reportWindow: '0d' };
    }
  }

  /**
   * Converts a UTC time string ("HH:mm") into a wall clock time.
   *
   * With a timeZone, the result is that zone's wall clock. Without one, it falls
   * back to the browser's own zone, which is the behaviour callers had before a
   * subscription carried its own zone.
   */
  convertUtcToLocalTime(utcTime: string, timeZone?: string): string {
    const { hours, minutes } = this.splitHoursAndMinutes(utcTime);

    if (timeZone) {
      const offset = this.getUtcOffsetMinutes(timeZone, this.buildInstantToday(hours, minutes));
      if (offset !== null) {
        return this.formatMinutesOfDay(hours * MINUTES_PER_HOUR + minutes + offset);
      }
    }

    const utcDate = new Date();
    utcDate.setUTCHours(hours, minutes, 0, 0);
    return this.formatHoursAndMinutes(utcDate.getHours(), utcDate.getMinutes());
  }

  /**
   * The abbreviation a zone uses on a given date, for example EST in January
   * and EDT in July for America/New_York.
   *
   * Follows the email-service report formatter (DPF-3227) so the dialog shows
   * the same abbreviation the email will:
   * - Intl resolves the name for the date, so daylight saving is handled.
   * - Some zones only have a short name in their own locale's data (Sydney is
   *   AEST under en-AU but GMT+10 under en-US), so the lookup uses the zone's
   *   abbreviationLocale.
   * - If Intl only returns an offset such as GMT+9, the zone's fixed
   *   abbreviation is used instead, but only when the zone has no daylight
   *   saving. A fixed name would be wrong for half the year in a zone that does
   *   (Azores alternates AZOT and AZOST), so those keep the offset.
   *
   * Returns null for an empty or unrecognised zone.
   */
  getTimeZoneAbbreviation(timeZoneId: string, instant: Date): string | null {
    if (!timeZoneId) {
      return null;
    }
    const definition = NOTIFICATION_TIME_ZONES.find(option => option.id === timeZoneId);

    let formatter = ABBREVIATION_FORMATTERS.get(timeZoneId);
    if (!formatter) {
      try {
        formatter = new Intl.DateTimeFormat(
          definition?.abbreviationLocale ?? DEFAULT_ABBREVIATION_LOCALE,
          { timeZone: timeZoneId, timeZoneName: 'short' }
        );
      } catch {
        return null;
      }
      ABBREVIATION_FORMATTERS.set(timeZoneId, formatter);
    }

    const resolved = formatter
      .formatToParts(instant)
      .find(part => part.type === 'timeZoneName')?.value;
    if (!resolved) {
      return definition?.abbreviation ?? null;
    }

    if (OFFSET_STYLE_ABBREVIATION.test(resolved) && definition && !this.observesDst(timeZoneId, instant)) {
      return definition.abbreviation;
    }
    return resolved;
  }

  /**
   * The time zone options for the dropdown, west to east.
   *
   * A zone without daylight saving is one option. A daylight-saving zone is two,
   * each pinned to a fixed offset all year: "(AEST) Sydney, Melbourne" is
   * UTC+10 and "(AEDT) Sydney, Melbourne" is UTC+11. A time entered against an
   * option therefore always means the offset its label names.
   *
   * Fixed offsets also match how a schedule actually fires: the saved cron is in
   * UTC and EventBridge never shifts it for daylight saving.
   */
  getTimeZoneOptions(instant: Date = new Date()): NotificationTimeZoneOption[] {
    const year = instant.getUTCFullYear();
    const options: NotificationTimeZoneOption[] = [];

    for (const zone of NOTIFICATION_TIME_ZONES) {
      const offsets = zone.daylightAbbreviation ? this.getStandardAndDaylightOffsets(zone.id, year) : null;
      const standardZone = offsets ? toFixedOffsetZone(offsets.standard) : null;
      const daylightZone = offsets ? toFixedOffsetZone(offsets.daylight) : null;

      if (zone.daylightAbbreviation && standardZone && daylightZone) {
        options.push(this.buildTimeZoneOption(zone, zone.abbreviation, standardZone));
        options.push(this.buildTimeZoneOption(zone, zone.daylightAbbreviation, daylightZone));
      } else {
        options.push(this.buildTimeZoneOption(zone, zone.abbreviation, zone.id));
      }
    }

    return options;
  }

  /**
   * The abbreviation a config should default to for a zone, for example AEDT
   * for Australia/Sydney in October and AEST in June. Matches the offset the
   * zone is on at the given instant, so a config saved before this field existed
   * keeps showing the same time. Returns null for an unknown zone.
   */
  resolveDefaultTimeZoneAbbreviation(timeZoneId: string, instant: Date = new Date()): string | null {
    const zone = NOTIFICATION_TIME_ZONES.find(option => option.id === timeZoneId);
    if (!zone) {
      return null;
    }
    if (!zone.daylightAbbreviation) {
      return zone.abbreviation;
    }

    const offsets = this.getStandardAndDaylightOffsets(timeZoneId, instant.getUTCFullYear());
    const currentOffset = this.getUtcOffsetMinutes(timeZoneId, instant);
    if (!offsets || currentOffset === null) {
      return zone.abbreviation;
    }
    return currentOffset === offsets.standard ? zone.abbreviation : zone.daylightAbbreviation;
  }

  private buildTimeZoneOption(
    zone: NotificationTimeZone,
    abbreviation: string,
    conversionTimeZone: string
  ): NotificationTimeZoneOption {
    return {
      value: `${zone.id}|${abbreviation}`,
      id: zone.id,
      abbreviation,
      region: zone.region,
      label: `(${abbreviation}) ${zone.region}`,
      conversionTimeZone
    };
  }

  /**
   * A zone's standard and daylight offsets for a year, from mid-January and
   * mid-July. Daylight saving only ever moves the clock forward, so the smaller
   * offset is standard in both hemispheres. Null when the zone is unrecognised.
   */
  private getStandardAndDaylightOffsets(
    timeZoneId: string,
    year: number
  ): { standard: number; daylight: number } | null {
    const januaryOffset = this.getUtcOffsetMinutes(timeZoneId, new Date(Date.UTC(year, 0, 15, 12)));
    const julyOffset = this.getUtcOffsetMinutes(timeZoneId, new Date(Date.UTC(year, 6, 15, 12)));
    if (januaryOffset === null || julyOffset === null) {
      return null;
    }
    return {
      standard: Math.min(januaryOffset, julyOffset),
      daylight: Math.max(januaryOffset, julyOffset)
    };
  }

  /** True when the zone's offset differs between January and July of the instant's year. */
  private observesDst(timeZoneId: string, instant: Date): boolean {
    const year = instant.getUTCFullYear();
    const januaryOffset = this.getUtcOffsetMinutes(timeZoneId, new Date(Date.UTC(year, 0, 15, 12)));
    const julyOffset = this.getUtcOffsetMinutes(timeZoneId, new Date(Date.UTC(year, 6, 15, 12)));
    return januaryOffset !== julyOffset;
  }

  /**
   * Resolves the supported time zone that best represents the machine's own
   * zone, for use when a config has no time zone saved yet.
   *
   * The machine's zone is rarely one of the curated options verbatim (a browser
   * in Toronto reports 'America/Toronto', not 'America/New_York'), so an exact
   * match alone would send most users to the fallback. Options are therefore
   * matched on standard-time offset, which lands the user on the option that
   * keeps the wall clock they already read times in.
   *
   * Standard time is compared rather than the offset in effect right now,
   * because during a daylight saving period a zone borrows its eastern
   * neighbour's offset: in July, Sao Paulo and Halifax both sit at UTC-03:00
   * despite belonging an hour apart. Comparing standard time keeps each machine
   * on its own offset and makes the result independent of the time of year.
   *
   * Where several options share a standard offset, one in the machine's own
   * region is preferred, so a browser in Dublin resolves to London, not UTC.
   *
   * FALLBACK_TIME_ZONE covers the offsets the curated list has no option for,
   * so the dropdown always opens on a real selection.
   */
  resolveDefaultTimeZone(): string {
    const machineTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (NOTIFICATION_TIME_ZONES.some(option => option.id === machineTimeZone)) {
      return machineTimeZone;
    }

    const year = new Date().getUTCFullYear();
    // getTimezoneOffset counts minutes behind UTC, the opposite of the
    // "ahead of UTC" convention used for comparison here.
    const machineStandardOffset = Math.min(
      -new Date(year, 0, 1).getTimezoneOffset(),
      -new Date(year, 6, 1).getTimezoneOffset()
    );

    const optionsAtSameOffset = NOTIFICATION_TIME_ZONES.filter(
      option => this.getStandardUtcOffsetMinutes(option.id, year) === machineStandardOffset
    );
    if (optionsAtSameOffset.length === 0) {
      return FALLBACK_TIME_ZONE;
    }

    const machineRegion = this.getTimeZoneRegion(machineTimeZone);
    const optionInSameRegion = optionsAtSameOffset.find(
      option => this.getTimeZoneRegion(option.id) === machineRegion
    );
    return (optionInSameRegion || optionsAtSameOffset[0]).id;
  }

  /**
   * Minutes the given zone is ahead of UTC outside any daylight saving period,
   * or null when the runtime does not recognise the identifier.
   *
   * Taken as the smaller of the zone's mid-January and mid-July offsets. Daylight
   * saving only ever advances the clock, so the smaller of the two is the zone's
   * standard offset whichever hemisphere it is in, and the two are equal for a
   * zone that does not observe it at all.
   */
  private getStandardUtcOffsetMinutes(timeZoneId: string, year: number): number | null {
    const januaryOffset = this.getUtcOffsetMinutes(timeZoneId, new Date(Date.UTC(year, 0, 15, 12)));
    const julyOffset = this.getUtcOffsetMinutes(timeZoneId, new Date(Date.UTC(year, 6, 15, 12)));
    if (januaryOffset === null || julyOffset === null) {
      return null;
    }
    return Math.min(januaryOffset, julyOffset);
  }

  /** The area part of an IANA identifier, for example 'Europe' in 'Europe/Paris'. */
  private getTimeZoneRegion(timeZoneId: string): string {
    return (timeZoneId || '').split('/')[0];
  }

  /**
   * Minutes the given zone is ahead of UTC at the given instant, or null when
   * the runtime does not recognise the identifier.
   *
   * Read by formatting the instant in the target zone and measuring the
   * resulting wall clock against the instant itself. The numeric date and time
   * fields used here have far broader browser support than asking Intl for the
   * offset directly via a 'longOffset' time zone name.
   */
  private getUtcOffsetMinutes(timeZoneId: string, instant: Date): number | null {
    let formattedParts: Intl.DateTimeFormatPart[];
    try {
      formattedParts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZoneId,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).formatToParts(instant);
    } catch {
      return null;
    }

    const field = (type: string) =>
      Number(formattedParts.find(part => part.type === type)?.value);

    const wallClockAsUtcMs = Date.UTC(
      field('year'),
      field('month') - 1,
      field('day'),
      // Guards against runtimes that report midnight as hour 24 under h23.
      field('hour') % HOURS_PER_DAY,
      field('minute')
    );
    // Both sides are truncated to the minute so the instant's own seconds,
    // which the formatted parts above omit, cannot skew the difference.
    const instantMs = Math.floor(instant.getTime() / MS_PER_MINUTE) * MS_PER_MINUTE;

    return (wallClockAsUtcMs - instantMs) / MS_PER_MINUTE;
  }

  /**
   * Converts a wall clock time ("HH:mm") to UTC.
   *
   * With a timeZone, the input is read as that zone's wall clock. Without one,
   * it falls back to the browser's own zone, preserving the behaviour callers
   * had before a subscription carried its own zone.
   */
  convertLocalToUtcTime(localTime: string, timeZone?: string): string {
    const { hours, minutes } = this.splitHoursAndMinutes(localTime);

    if (timeZone) {
      const offset = this.getWallTimeOffsetMinutes(timeZone, hours, minutes);
      if (offset !== null) {
        return this.formatMinutesOfDay(hours * MINUTES_PER_HOUR + minutes - offset);
      }
    }

    const localDate = new Date();
    localDate.setHours(hours, minutes, 0, 0);
    return this.formatHoursAndMinutes(localDate.getUTCHours(), localDate.getUTCMinutes());
  }

  /**
   * How many days the UTC conversion of a wall clock time moves it: -1 when the
   * UTC time falls on the previous day, 1 on the next day, 0 on the same day.
   *
   * Resolves the zone the same way convertLocalToUtcTime does, so the shift
   * always agrees with the converted time it accompanies.
   */
  getUtcDayShift(localTime: string, timeZone?: string): number {
    const { hours, minutes } = this.splitHoursAndMinutes(localTime);
    const minutesPerDay = HOURS_PER_DAY * MINUTES_PER_HOUR;

    if (timeZone) {
      const offset = this.getWallTimeOffsetMinutes(timeZone, hours, minutes);
      if (offset !== null) {
        return Math.floor((hours * MINUTES_PER_HOUR + minutes - offset) / minutesPerDay);
      }
    }

    const localDate = new Date();
    localDate.setHours(hours, minutes, 0, 0);
    const localDayMs = Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate());
    const utcDayMs = Date.UTC(
      localDate.getUTCFullYear(),
      localDate.getUTCMonth(),
      localDate.getUTCDate()
    );
    return Math.round((utcDayMs - localDayMs) / (minutesPerDay * MS_PER_MINUTE));
  }

  /** Moves a cron day-of-week token by the given number of days, wrapping the week. */
  private shiftCronWeekDay(cronWeekDay: string, dayShift: number): string {
    const dayIndex = CRON_WEEK_DAYS.indexOf(cronWeekDay);
    if (dayIndex === -1) {
      return cronWeekDay;
    }
    const daysPerWeek = CRON_WEEK_DAYS.length;
    return CRON_WEEK_DAYS[(((dayIndex + dayShift) % daysPerWeek) + daysPerWeek) % daysPerWeek];
  }

  /** Splits an "HH:mm" string into its numeric hour and minute. */
  private splitHoursAndMinutes(time: string): { hours: number; minutes: number } {
    const [hours, minutes] = time.split(':').map(Number);
    return { hours, minutes };
  }

  /** Formats an hour and minute as a zero-padded "HH:mm". */
  private formatHoursAndMinutes(hours: number, minutes: number): string {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  /** Formats minutes past midnight as "HH:mm", wrapping across day boundaries. */
  private formatMinutesOfDay(minutesOfDay: number): string {
    const minutesPerDay = HOURS_PER_DAY * MINUTES_PER_HOUR;
    const wrapped = ((minutesOfDay % minutesPerDay) + minutesPerDay) % minutesPerDay;
    return this.formatHoursAndMinutes(
      Math.floor(wrapped / MINUTES_PER_HOUR),
      wrapped % MINUTES_PER_HOUR
    );
  }

  /** Today's date at the given UTC hour and minute. */
  private buildInstantToday(utcHours: number, utcMinutes: number): Date {
    const now = new Date();
    return new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHours, utcMinutes)
    );
  }

  /**
   * Offset of the given zone at the instant its wall clock reads the given time
   * today, or null when the runtime does not recognise the zone.
   *
   * The offset and the instant depend on each other, so a first guess treats the
   * wall time as UTC and a second pass re-reads the offset at the instant that
   * guess maps to. That settles the answer either side of a DST boundary.
   */
  private getWallTimeOffsetMinutes(
    timeZone: string,
    hours: number,
    minutes: number
  ): number | null {
    const guessedInstant = this.buildInstantToday(hours, minutes);
    const guessedOffset = this.getUtcOffsetMinutes(timeZone, guessedInstant);
    if (guessedOffset === null) {
      return null;
    }

    const refinedInstant = new Date(guessedInstant.getTime() - guessedOffset * MS_PER_MINUTE);
    return this.getUtcOffsetMinutes(timeZone, refinedInstant) ?? guessedOffset;
  }

  // ── Config CRUD (DynamoDB workaround) ──

  /**
   * Builds a new notification config object populated with default values.
   *
   * This is a pure factory: it performs no IO and persists nothing. Callers
   * hold the returned object in memory and persist it via
   * {@link createNotification} only when the user actually saves or opts in.
   * This avoids creating a DynamoDB item as a side effect of merely
   * reading/opening the dialog.
   */
  buildNewConfig(assetID: string, cognitoId: string, createdBy: string, company: string): NotificationConfig {
    const defaultTimeZone = this.resolveDefaultTimeZone();
    return {
      assetID,
      notificationId: uuid(),
      cognitoID: cognitoId,
      company,
      product: DEFAULT_PRODUCT,
      reportType: DEFAULT_REPORT_TYPE,
      filter: DEFAULT_FILTER,
      notificationType: DEFAULT_NOTIFICATION_TYPE,
      emails: [],
      isActive: false,
      schedule: null,
      reportWindow: null,
      lastSentAt: null,
      eventBridgeRuleArn: null,
      version: INITIAL_VERSION,
      createdBy,
      createdAt: new Date().toISOString(),
      lastModified: '',
      time: DEFAULT_TIME_OF_DAY,
      timeZone: defaultTimeZone,
      timeZoneAbbreviation: this.resolveDefaultTimeZoneAbbreviation(defaultTimeZone) ?? undefined,
      weekDay: '',
      timeInterval: ''
    };
  }

  /**
   * Builds the fields both write routes send, applying a default wherever the
   * config leaves one blank.
   *
   * version and createdAt are passed straight through here: only the create
   * route seeds them, and it overrides them after this runs.
   */
  private buildRequestPayload(config: NotificationConfig): NotificationRequestPayload {
    return {
      cognitoID: config.cognitoID,
      company: config.company,
      product: config.product || DEFAULT_PRODUCT,
      reportType: config.reportType || DEFAULT_REPORT_TYPE,
      filter: config.filter || DEFAULT_FILTER,
      notificationType: config.notificationType || DEFAULT_NOTIFICATION_TYPE,
      emails: config.emails || [],
      isActive: config.isActive,
      schedule: config.schedule || null,
      reportWindow: config.reportWindow || null,
      lastSentAt: config.lastSentAt || null,
      eventBridgeRuleArn: config.eventBridgeRuleArn || null,
      version: config.version,
      createdBy: config.createdBy,
      createdAt: config.createdAt,
      lastModified: config.lastModified || new Date().toISOString(),
      time: config.time,
      timeZone: config.timeZone || this.resolveDefaultTimeZone(),
      timeZoneAbbreviation:
        config.timeZoneAbbreviation ||
        this.resolveDefaultTimeZoneAbbreviation(config.timeZone || this.resolveDefaultTimeZone()) ||
        '',
      weekDay: config.weekDay || '',
      timeInterval: config.timeInterval || ''
    };
  }

  /**
   * Creates a new notification configuration via the notifications API.
   *
   * POST /notifications
   * The backend generates a notificationId and creates an EventBridge rule
   * if the notification type is 'scheduled'.
   */
  createNotification(config: NotificationConfig): Observable<any> {
    const url = `${this.baseUrl}/notifications`;
    const body: CreateNotificationRequest = {
      assetID: config.assetID,
      notificationId: config.notificationId,
      ...this.buildRequestPayload(config),
      // A record created for the first time gets these seeded; an update must
      // send back exactly what it was given, for the optimistic lock.
      version: config.version || INITIAL_VERSION,
      createdAt: config.createdAt || new Date().toISOString()
    };
    return this.getAuthHeaders().pipe(
      switchMap(headers => this.http.post(url, body, { headers }))
    );
  }

  /**
   * Updates an existing notification configuration via the notifications API.
   *
   * PUT /notifications/{assetID}/{notificationId}
   * Uses optimistic locking via the version field to prevent lost updates.
   */
  updateNotification(config: NotificationConfig): Observable<any> {
    const url = `${this.baseUrl}/notifications/${config.assetID}/${config.notificationId}`;
    const body: UpdateNotificationRequest = this.buildRequestPayload(config);
    return this.getAuthHeaders().pipe(
      switchMap(headers => this.http.put(url, body, { headers }))
    );
  }
}
